import { useId } from 'react';
import { FeatureUnavailableNotice } from '../../../shared/components/FeatureUnavailableNotice';
import type { CheckBatch } from '../types';
import { formatDate, formatMoney, formatPeriod, plural } from '../format';
import { CheckDeliveryNote } from './TrialBanner';

interface ReviewStepProps {
  batch: CheckBatch;
  confirmed: boolean;
  onConfirmedChange: (value: boolean) => void;
  /** Why Approve is unavailable even after the box is ticked (stale readiness, blockers, unacknowledged warnings), or null. */
  blockedReason: string | null;
  /** False in a production bundle: the approve action is not offered at all (EPN-98). */
  approveAvailable: boolean;
  busy: boolean;
  onBack: () => void;
  onDiscard: () => void;
  onSaveDraft: () => void;
  onApprove: () => void;
  onRecheck: () => void;
}

/** Step 3. Totals are the server's; per-employee amounts are listed so each payment can be checked, not just the batch total. */
export function ReviewStep(props: ReviewStepProps) {
  const { batch, confirmed, blockedReason, approveAvailable, busy } = props;
  const checkId = useId();
  const included = batch.lines.filter((l) => l.included);
  const canApprove = confirmed && blockedReason === null && approveAvailable && !busy;

  return (
    <section aria-labelledby="wc-review-title">
      <CheckDeliveryNote />
      <h3 id="wc-review-title">Review Payslip</h3>
      <dl className="wc-cards">
        <div><dt>Pay Period</dt><dd>{formatPeriod(batch.periodStartDate, batch.periodEndDate)}</dd></div>
        <div><dt>Pay Date</dt><dd>{formatDate(batch.payDate)}</dd></div>
        <div><dt>Payment Method</dt><dd>Write Check</dd></div>
        <div><dt>Employees Selected</dt><dd data-testid="review-employees">{plural(batch.totals.employeesIncluded, 'Employee', 'Employees')}</dd></div>
        <div><dt>Gross Pay Total</dt><dd data-testid="review-gross">{formatMoney(batch.totals.grossTotal)}</dd></div>
        <div><dt>Net Pay Total</dt><dd data-testid="review-net">{formatMoney(batch.totals.netTotal)}</dd></div>
        <div><dt>Check Total</dt><dd className="wc-accent" data-testid="review-check">{formatMoney(batch.totals.checkTotal)}</dd></div>
        <div><dt>Check Numbers</dt><dd data-testid="review-numbers">{included.map((l) => l.checkNumber).join(', ')}</dd></div>
      </dl>

      <h4 className="wc-subhead">Payments in this batch</h4>
      <div className="table-scroll" role="region" aria-label="Payments in this batch" tabIndex={0}>
        <table className="wc-table wc-table--compact" aria-label="Payments in this batch">
          <thead>
            <tr><th scope="col">Employee</th><th scope="col">Check Number</th><th scope="col" className="wc-num">Check Amount</th></tr>
          </thead>
          <tbody>
            {included.map((l) => (
              <tr key={l.id}><td>{l.employeeName}</td><td>{l.checkNumber}</td><td className="wc-num">{formatMoney(l.checkAmount)}</td></tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="wc-confirm">
        <p className="wc-confirm__label">Before you approve:</p>
        <label className="wc-check" htmlFor={checkId}>
          <input id={checkId} type="checkbox" checked={confirmed} disabled={busy} onChange={(e) => props.onConfirmedChange(e.target.checked)} />
          <span>I confirm that I have reviewed all payment details, and that the amounts, recipients, and pay dates are correct to the best of my knowledge.</span>
        </label>
      </div>

      {blockedReason && (
        <p className="wc-warn" role="status" data-testid="approve-blocked">
          {blockedReason}{' '}
          <button type="button" className="wc-link" onClick={props.onRecheck}>Go to the readiness check</button>
        </p>
      )}
      {!approveAvailable && (
        <FeatureUnavailableNotice feature="Approving a check batch">
          Approval is not available in this release. Nothing has been approved, printed, mailed or paid.
        </FeatureUnavailableNotice>
      )}

      <div className="wc-footer wc-footer--review">
        <button type="button" className="wc-btn wc-btn--outline" onClick={props.onBack} disabled={busy}>Back to Readiness Check</button>
        <div className="wc-footer__actions">
          <button type="button" className="wc-link wc-link--muted" onClick={props.onDiscard} disabled={busy}>Discard</button>
          <button type="button" className="wc-btn wc-btn--green" onClick={props.onSaveDraft} disabled={busy}>Save as Draft</button>
          {approveAvailable && (
            <button type="button" className="wc-btn wc-btn--primary" onClick={props.onApprove} disabled={!canApprove}>Approve Payslip</button>
          )}
        </div>
      </div>
    </section>
  );
}
